"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WRITE_RATE_LIMITED = exports.WRITE_OFFLINE = exports.WRITE_GONE = void 0;
exports.writeFailureReason = writeFailureReason;
exports.writeFailureCopy = writeFailureCopy;
exports.writeBatchSummary = writeBatchSummary;
/**
 * What both apps say when a write fails (BEN-141 FP-103).
 *
 * The flow pass found one rule drifting across many sites: web had 25 writes
 * that failed silently and 17 that each worded their own toast; mobile handled
 * 68 of 70 in about a dozen hand-rolled ways. The contract (followups
 * 2026-10-06-74) puts the reporting in ONE place per client, the QueryClient's
 * MutationCache, and the words here, so the two apps cannot drift again.
 *
 * Every mutation declares `meta.write`:
 * - `action` follows "Couldn't": "save the debt" → "Couldn't save the debt."
 * - `unchanged` is what is still true when the server gave no reason. The site
 *   writes it, because only the site knows: a create's truth is "Nothing was
 *   added.", not "hasn't changed".
 * - `report` is who reports it. "global": the cache (a toast on web, appAlert
 *   on mobile). "inline": the site, next to the input or as a batch summary,
 *   and the cache stays silent. A write is never reported twice; that is part
 *   of the definition, not a nicety (mobile's ~20 inline forms).
 */
const httpStatus_1 = require("./httpStatus");
/** Said for 404/410, or a 409 without a reason: the thing is gone, so "hasn't changed" would be untrue. */
exports.WRITE_GONE = "It was already removed.";
/** Appended when the request never got an answer. */
exports.WRITE_OFFLINE = "Check your connection and try again.";
/**
 * Said for 429, in place of the server's throttle detail ("Request was throttled.
 * Expected available in N seconds.", which isn't written for people). Paul,
 * 2026-10-06: a rate limit isn't a failure the user caused, and "try again" is the
 * right advice here, unlike everywhere else.
 */
exports.WRITE_RATE_LIMITED = "Too many changes in a short time. Wait a minute, then try again.";
/**
 * The error body, from either client: web's axios keeps it at
 * `error.response.data`, mobile's ApiClientError at `error.errors`.
 */
function errorBody(error) {
    if (error == null || typeof error !== "object")
        return undefined;
    const e = error;
    const body = e.response?.data ?? e.errors;
    return body && typeof body === "object" && !Array.isArray(body)
        ? body
        : undefined;
}
function firstString(value) {
    if (typeof value === "string")
        return value.trim() || undefined;
    if (Array.isArray(value)) {
        for (const item of value) {
            const found = firstString(item);
            if (found)
                return found;
        }
    }
    return undefined;
}
/**
 * The server's reason, when it wrote one: `detail`, else the first field error
 * (`non_field_errors` first). The second half is FP-090: a 400 that names the
 * problem used to reach the screen as a generic failure.
 */
function writeFailureReason(error) {
    const body = errorBody(error);
    if (!body)
        return undefined;
    const detail = firstString(body.detail);
    if (detail)
        return detail;
    const general = firstString(body.non_field_errors);
    if (general)
        return general;
    for (const [key, value] of Object.entries(body)) {
        if (key === "code" || key === "versions")
            continue;
        const found = firstString(value);
        if (found)
            return found;
    }
    return undefined;
}
/**
 * A request that never got an answer: axios's ERR_NETWORK or a timeout on web,
 * fetch's TypeError on mobile. An answer that could not be read (web's
 * MalformedResponse) or a fault in the app's own code has no status either, and
 * "check your connection" would send the user after the wrong thing.
 */
function isNetworkFailure(error) {
    if (error instanceof TypeError)
        return true;
    const code = error?.code;
    return code === "ERR_NETWORK" || code === "ECONNABORTED";
}
/** The title and body for one failed write. */
function writeFailureCopy(meta, error) {
    const title = `Couldn't ${meta.action}.`;
    const status = (0, httpStatus_1.httpErrorStatus)(error);
    if (status === 429)
        return { title, body: exports.WRITE_RATE_LIMITED };
    const reason = writeFailureReason(error);
    if (reason)
        return { title, body: reason };
    if (status === 404 || status === 409 || status === 410)
        return { title, body: exports.WRITE_GONE };
    if (status === undefined && isNetworkFailure(error)) {
        return { title, body: `${meta.unchanged} ${exports.WRITE_OFFLINE}` };
    }
    return { title, body: meta.unchanged };
}
/**
 * A save made of several writes, some of which failed: "1 of 2 saved." The
 * site reports this itself (its writes declare "inline"), so N failed legs
 * never become N alerts.
 */
function writeBatchSummary(saved, total) {
    const failed = total - saved;
    let body;
    if (saved === 0)
        body = "Nothing has changed.";
    else if (failed === 1)
        body = total === 2 ? "The other one hasn't changed." : "1 hasn't changed.";
    else
        body = `The other ${failed} haven't changed.`;
    return { title: `${saved} of ${total} saved.`, body };
}
//# sourceMappingURL=writeFailureCopy.js.map